import { useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './AgendaAppointmentInfo.css';

export interface AgendaAppointmentInfoData {
  id: string;
  title: string;
  patientName: string;
  dateOfBirth: string;
  description: string;
  when: string;
  duration: number;
  practitionerLabel: string;
  color?: { backgroundColor: string; borderColor: string; textColor: string };
}

const time = (date: Date) => date.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
const appointmentTimes = (appointment: AgendaAppointmentInfoData) => {
  const start = new Date(appointment.when);
  const end = new Date(start.getTime() + appointment.duration * 60000);
  return { start, end, crossesDate: start.toDateString() !== end.toDateString() };
};
const date = (value: Date) => value.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });

export function AgendaAppointmentPreview({ appointment, anchor }: {
  appointment: AgendaAppointmentInfoData;
  anchor: { x: number; y: number };
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  const { start, end, crossesDate } = appointmentTimes(appointment);

  useLayoutEffect(() => {
    const place = () => {
      if (!ref.current) return;
      const viewport = window.visualViewport;
      const leftEdge = (viewport?.offsetLeft ?? 0) + 8;
      const topEdge = (viewport?.offsetTop ?? 0) + 8;
      const right = leftEdge + (viewport?.width ?? window.innerWidth) - 16;
      const bottom = topEdge + (viewport?.height ?? window.innerHeight) - 16;
      ref.current.style.maxWidth = `${Math.max(0, right - leftEdge)}px`;
      ref.current.style.maxHeight = `${Math.max(0, bottom - topEdge)}px`;
      const bounds = ref.current.getBoundingClientRect();
      const left = anchor.x + 14 + bounds.width <= right ? anchor.x + 14 : anchor.x - bounds.width - 14;
      const top = anchor.y + 14 + bounds.height <= bottom ? anchor.y + 14 : anchor.y - bounds.height - 14;
      setPosition({ left: Math.max(leftEdge, Math.min(left, right - bounds.width)), top: Math.max(topEdge, Math.min(top, bottom - bounds.height)) });
    };
    place();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(place);
    if (ref.current) observer?.observe(ref.current);
    window.addEventListener('resize', place);
    window.visualViewport?.addEventListener('resize', place);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', place);
      window.visualViewport?.removeEventListener('resize', place);
    };
  }, [anchor]);

  return createPortal(
    <div ref={ref} id="agenda-appointment-preview" role="tooltip" className="agenda-appointment-info agenda-appointment-preview"
      style={{ left: position?.left ?? 0, top: position?.top ?? 0, visibility: position ? 'visible' : 'hidden' }}>
      <dl>
        <dt>Patient:</dt><dd>{appointment.patientName || '–'}</dd>
        <dt>Behandlung:</dt><dd>{appointment.title || '–'}</dd>
        <dt>Beschreibung:</dt><dd>{appointment.description || '–'}</dd>
        <dt>Beginn:</dt><dd>{time(start)} Uhr</dd>
        <dt>Ende:</dt><dd>{crossesDate ? `${date(end)} · ` : ''}{time(end)} Uhr</dd>
      </dl>
    </div>, document.body
  );
}

export function AgendaAppointmentDetails({ appointment, onClose, onEdit, onDelete, onMove, preparingMove, moveError, suspended }: {
  appointment: AgendaAppointmentInfoData;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onMove?: () => void;
  preparingMove: boolean;
  moveError: string;
  suspended: boolean;
}) {
  const nameId = useId();
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const { start, end, crossesDate } = appointmentTimes(appointment);

  useLayoutEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => { if (previous?.isConnected) previous.focus(); };
  }, []);

  return (
    <div className="agenda-appointment-overlay" onClick={onClose}>
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby={nameId} aria-hidden={suspended || undefined}
        className="agenda-appointment-info agenda-appointment-details" onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (suspended) return;
          if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); onClose(); }
          if (event.key === 'Tab') {
            const buttons = ref.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)');
            const first = buttons?.[0], last = buttons?.[buttons.length - 1];
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
            if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
          }
        }}>
        <button ref={closeRef} type="button" className="agenda-appointment-details__close" aria-label="Termindetails schliessen" onClick={onClose}>
          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="m6 6 8 8M14 6l-8 8" /></svg>
        </button>
        <div className="agenda-appointment-details__content">
          <p id={nameId} className="agenda-appointment-details__patient">{appointment.patientName || '–'}</p>
          <p className="agenda-appointment-details__treatment">{appointment.title || '–'}</p>
          {appointment.description ? <p className="agenda-appointment-details__description">{appointment.description}</p> : null}
          <dl className="agenda-appointment-details__times">
            <dt>Datum</dt><dd>{date(start)}</dd>
            <dt>Beginn</dt><dd>{time(start)} Uhr</dd>
            <dt>Ende</dt><dd>{crossesDate ? `${date(end)} · ` : ''}{time(end)} Uhr</dd>
            <dt>Dauer</dt><dd>{appointment.duration} Minuten</dd>
          </dl>
          <dl className="agenda-appointment-details__metadata">
            {appointment.dateOfBirth ? <><dt>Geburtsdatum</dt><dd>{appointment.dateOfBirth}</dd></> : null}
            {appointment.practitionerLabel ? <><dt>Behandler</dt><dd>{appointment.practitionerLabel}</dd></> : null}
          </dl>
        </div>
        <div className="agenda-appointment-details__actions">
          <button type="button" onClick={onEdit}>Ändern</button>
          <button type="button" className="agenda-appointment-details__delete" onClick={onDelete}>Termin löschen</button>
          {onMove ? <button type="button" className="agenda-appointment-details__move" onClick={onMove} disabled={preparingMove}>{preparingMove ? 'Termin wird geladen…' : 'Termin verschieben'}</button> : null}
        </div>
        {moveError ? <p className="agenda-appointment-details__move-error" role="alert">{moveError}</p> : null}
      </div>
    </div>
  );
}
